import {flatOuterCardSx, flatOuterCardContentSx} from '../../../utils/mobileCardSx';
import * as React from "react";
import {
	Avatar,
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Pagination,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CalculateIcon from "@mui/icons-material/Calculate";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useTranslation } from "react-i18next";
import { MobilePeriodBranchFilterBar } from "../../Dashboard/MobilePeriodBranchFilterBar";
import { getAvatarColors, getInitials } from "../../../utils/avatarUtils";
import { type PayrollCalculation, usePayrollRun } from "../../../hooks/usePayrollRun";
import { PAYROLL_RUN_COLUMNS, getPayrollRunColumnValue } from "./payrollRunColumns";
import PayrollRunMobile from "./mobile/PayrollRunMobile";
import PayrollRunDetailsPerEmployee from "./dialogs/PayrollRunDetailsPerEmployee";
import PayrollRunColumnsDialog from "./dialogs/PayrollRunColumnsDialog";
import PayrollRunSummary from "./summary/PayrollRunSummary";
import PayrollRunSummaryMobile from "./summary/PayrollRunSummaryMobile";
import PayrollRunReconciliation from "./summary/PayrollRunReconciliation";
import PayrollRunReconciliationMobile from "./summary/PayrollRunReconciliationMobile";
import PayrollRunStatusRow from "./PayrollRunStatusRow";

// Fallback used until the sticky mobile action bar's real height is measured (see
// `bottomBarHeight` below) — comfortably larger than its typical single-row height so there's no
// visible jump/flash before the ResizeObserver reports the actual value.
const DEFAULT_BOTTOM_BAR_CLEARANCE = 96;

export default function PayrollRun(): React.ReactElement {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const {
		selectedPeriod,
		isPeriodOpen,
		isPeriodLocked,
		isFinalizeDisabled,
		isAllBranchesSelected,
		isPeriodLockedNotYetFinalized,
		searchInput,
		setSearchInput,
		setSearchQuery,
		currentPage,
		setCurrentPage,
		periodError,
		totalGross,
		totalTax,
		totalPensionEmp,
		totalPensionEmpr,
		employeeCount,
		totalBankNet,
		totalEmployerCost,
		reconciliation,
		contracts,
		detailsDialogOpen,
		setDetailsDialogOpen,
		selectedEmployee,
		setSelectedEmployee,
		itemsPerPage,
		order,
		orderBy,
		handleRequestSort,
		sortedAndFilteredCalculations,
		formatEUR,
		isColumnVisible,
		handleToggleColumnVisibility,
		handleShowAllColumns,
		visibleColumnCount,
		handleCalculatePayroll,
		calculating,
		downloadPayroll,
		downloading,
		finalizeDialogOpen,
		finalizing,
		finalizeError,
		finalizeInfo,
		finalizeSuccessMessage,
		setFinalizeSuccessMessage,
		handleOpenFinalizeDialog,
		handleCloseFinalizeDialog,
		handleConfirmFinalize,
	} = usePayrollRun();

	const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState<HTMLElement | null>(null);
	const [columnsDialogOpen, setColumnsDialogOpen] = React.useState(false);

	// Measures the sticky mobile action bar's real rendered height so the scrollable content
	// always reserves exactly enough bottom clearance — a fixed guess (previously a static `pb`)
	// wasn't reliable across devices/font sizes and ended up covering the last reconciliation row.
	const bottomBarRef = React.useRef<HTMLDivElement | null>(null);
	const [bottomBarHeight, setBottomBarHeight] = React.useState(DEFAULT_BOTTOM_BAR_CLEARANCE);

	React.useLayoutEffect(() => {
		if (!isMobile || !bottomBarRef.current) return;
		const el = bottomBarRef.current;
		const updateHeight = () => setBottomBarHeight(el.offsetHeight);
		updateHeight();
		const observer = new ResizeObserver(updateHeight);
		observer.observe(el);
		return () => observer.disconnect();
	}, [isMobile]);

	const periodStatusLabel = isPeriodOpen ? t('common.periodStatus.open') : isPeriodLocked ? t('common.periodStatus.locked') : undefined;

	const handleViewDetails = (employee: PayrollCalculation) => {
		setSelectedEmployee(employee);
		setDetailsDialogOpen(true);
	};

	return (
		<>
			{isMobile && <MobilePeriodBranchFilterBar/>}

			<Box sx={{ width: "100%", pb: isMobile ? `${bottomBarHeight + 24}px` : 3 }}>
				<PayrollRunStatusRow
					isPeriodOpen={isPeriodOpen}
					isPeriodLocked={isPeriodLocked}
					hasResults={sortedAndFilteredCalculations.length > 0}
				/>

				{periodError && (
					<Typography variant="body2" color="error" sx={{ mb: 2, textAlign: "left" }}>
						{periodError}
					</Typography>
				)}

				{isPeriodLockedNotYetFinalized && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						{t('payroll.run.periodLockedWarning')}
					</Alert>
				)}

				<Box sx={{ mb: 2 }}>
					{isMobile ? (
						<PayrollRunSummaryMobile
							employeeCount={employeeCount}
							totalGross={totalGross}
							totalBankNet={totalBankNet}
							totalEmployerCost={totalEmployerCost}
							formatEUR={formatEUR}
						/>
					) : (
						<PayrollRunSummary
							employeeCount={employeeCount}
							totalGross={totalGross}
							totalTax={totalTax}
							totalPensionEmp={totalPensionEmp}
							totalPensionEmpr={totalPensionEmpr}
							totalBankNet={totalBankNet}
							totalEmployerCost={totalEmployerCost}
							formatEUR={formatEUR}
						/>
					)}
				</Box>

				<Card variant="outlined" sx={flatOuterCardSx(isMobile)}>
					<CardContent sx={flatOuterCardContentSx(isMobile)}>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							justifyContent="space-between"
							alignItems={{ xs: "flex-start", sm: "center" }}
							spacing={1}
							sx={{ mb: 2 }}
						>
							<Typography variant="h6" fontWeight={700}>
								{t('payroll.run.table.title')}
							</Typography>
							{!isMobile && (
								<Stack direction="row" spacing={1.5}>
									<Button
										variant="outlined"
										onClick={downloadPayroll}
										disabled={downloading}
										startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
									>
										{downloading ? t('common.downloading') : t('common.actions.download')}
									</Button>
									<Button
										variant="contained"
										onClick={handleCalculatePayroll}
										disabled={calculating}
										startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
									>
										{calculating ? t('payroll.run.calculating') : t('payroll.run.calculateButton')}
									</Button>
								</Stack>
							)}
						</Stack>

						<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
							<TextField
								fullWidth
								size="small"
								label={t('payroll.run.searchLabel')}
								variant="outlined"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										setSearchQuery(searchInput);
									}
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon color="action" />
										</InputAdornment>
									),
									endAdornment: searchInput ? (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onClick={() => {
													setSearchInput("");
													setSearchQuery("");
												}}
											>
												<ClearIcon fontSize="small" />
											</IconButton>
										</InputAdornment>
									) : null,
								}}
							/>
							<Button
								variant="outlined"
								startIcon={<ViewColumnIcon />}
								onClick={() => setColumnsDialogOpen(true)}
								sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
							>
								{t('payroll.run.columnsButton', { count: visibleColumnCount })}
							</Button>
						</Stack>

						{/* Employee Table */}
						{isMobile ? (
							<PayrollRunMobile
								employees={sortedAndFilteredCalculations}
								currentPage={currentPage}
								itemsPerPage={itemsPerPage}
								formatEUR={formatEUR}
								onViewDetails={handleViewDetails}
								isColumnVisible={isColumnVisible}
							/>
						) : (
							<TableContainer sx={{ mb: 2, overflowX: "auto" }}>
								<Table sx={{ minWidth: { xs: 500, md: 750 } }}>
									<TableHead>
										<TableRow>
											<TableCell sortDirection={orderBy === "name" ? order : false}>
												<TableSortLabel
													active={orderBy === "name"}
													direction={orderBy === "name" ? order : "asc"}
													onClick={() => handleRequestSort("name")}
												>
													{t('payroll.run.table.header.name')}
												</TableSortLabel>
											</TableCell>
											{isColumnVisible("branch") && (
												<TableCell sortDirection={orderBy === "branch" ? order : false}>
													<TableSortLabel
														active={orderBy === "branch"}
														direction={orderBy === "branch" ? order : "asc"}
														onClick={() => handleRequestSort("branch")}
													>
														{t('hr.jobs.table.header.branch')}
													</TableSortLabel>
												</TableCell>
											)}
											{isColumnVisible("grossBase") && (
												<TableCell align="right">{t('payroll.run.table.header.grossBase')}</TableCell>
											)}
											{isColumnVisible("adjustments") && (
												<TableCell align="right">
													<Box sx={{display: "inline-flex", alignItems: "center", gap: 0.5}}>
														{t('payroll.run.table.header.adjustments')}
														<Tooltip title={t('payroll.run.table.header.adjustmentsTooltip')}>
															<InfoOutlinedIcon fontSize="inherit" color="action"/>
														</Tooltip>
													</Box>
												</TableCell>
											)}
											{isColumnVisible("finalGross") && (
												<TableCell sortDirection={orderBy === "gross" ? order : false} align="right">
													<TableSortLabel
														active={orderBy === "gross"}
														direction={orderBy === "gross" ? order : "asc"}
														onClick={() => handleRequestSort("gross")}
													>
														{t('payroll.run.table.header.finalGross')}
													</TableSortLabel>
												</TableCell>
											)}
											{isColumnVisible("pensionEmp") && (
												<TableCell sortDirection={orderBy === "pension" ? order : false} align="right">
													<TableSortLabel
														active={orderBy === "pension"}
														direction={orderBy === "pension" ? order : "asc"}
														onClick={() => handleRequestSort("pension")}
													>
														{t('payroll.run.table.header.pensionEmp')}
													</TableSortLabel>
												</TableCell>
											)}
											{isColumnVisible("tax") && (
												<TableCell sortDirection={orderBy === "tax" ? order : false} align="right">
													<TableSortLabel
														active={orderBy === "tax"}
														direction={orderBy === "tax" ? order : "asc"}
														onClick={() => handleRequestSort("tax")}
													>
														{t('payroll.run.table.header.tax')}
													</TableSortLabel>
												</TableCell>
											)}
											{isColumnVisible("otherDeductions") && (
												<TableCell align="right">{t('payroll.run.table.header.otherDeductions')}</TableCell>
											)}
											{isColumnVisible("netToBank") && (
												<TableCell sortDirection={orderBy === "bankNet" ? order : false} align="right">
													<TableSortLabel
														active={orderBy === "bankNet"}
														direction={orderBy === "bankNet" ? order : "asc"}
														onClick={() => handleRequestSort("bankNet")}
													>
														{t('payroll.run.table.header.netToBank')}
													</TableSortLabel>
												</TableCell>
											)}
											{isColumnVisible("status") && (
												<TableCell align="center">{t('hr.branches.table.header.status')}</TableCell>
											)}
											<TableCell align="center">{t('payroll.run.table.header.details')}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{sortedAndFilteredCalculations.length === 0 ? (
											<TableRow>
												<TableCell colSpan={2 + PAYROLL_RUN_COLUMNS.length} align="center" sx={{ py: 6 }}>
													<CalculateIcon
														sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
													/>
													<Typography variant="body1" color="text.secondary">
														{t('payroll.run.table.empty.title')}
													</Typography>
													<Typography variant="body2" color="text.disabled">
														{t('payroll.run.table.empty.description')}
													</Typography>
												</TableCell>
											</TableRow>
										) : (
											sortedAndFilteredCalculations
												.slice(
													(currentPage - 1) * itemsPerPage,
													currentPage * itemsPerPage,
												)
												.map((employee) => {
													const [employeeFirstName, ...employeeLastNameParts] = employee.name.split(' ');
													const employeeLastName = employeeLastNameParts.join(' ');
													const avatarColors = getAvatarColors(`${employee.name}${employee.employeeNumber}`);
													return (
														<TableRow key={employee.employeeNumber}>
															<TableCell>
																<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
																	<Avatar sx={{
																		width: 36,
																		height: 36,
																		bgcolor: avatarColors.bg,
																		color: avatarColors.color,
																		fontSize: "0.875rem",
																		fontWeight: 600,
																	}}>
																		{getInitials(employeeFirstName, employeeLastName)}
																	</Avatar>
																	<Typography variant="body2" fontWeight={600}>
																		{employee.name}
																	</Typography>
																</Box>
															</TableCell>
															{isColumnVisible("branch") && (
																<TableCell>{getPayrollRunColumnValue(employee, "branch", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("grossBase") && (
																<TableCell align="right">{getPayrollRunColumnValue(employee, "grossBase", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("adjustments") && (
																<TableCell align="right">{getPayrollRunColumnValue(employee, "adjustments", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("finalGross") && (
																<TableCell align="right">{getPayrollRunColumnValue(employee, "finalGross", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("pensionEmp") && (
																<TableCell align="right">{getPayrollRunColumnValue(employee, "pensionEmp", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("tax") && (
																<TableCell align="right">{getPayrollRunColumnValue(employee, "tax", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("otherDeductions") && (
																<TableCell align="right">{getPayrollRunColumnValue(employee, "otherDeductions", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("netToBank") && (
																<TableCell align="right">{getPayrollRunColumnValue(employee, "netToBank", formatEUR, t)}</TableCell>
															)}
															{isColumnVisible("status") && (
																<TableCell align="center">
																	<Chip label={t('payroll.run.statusOk')} size="small" color="success" variant="outlined"/>
																</TableCell>
															)}
															<TableCell align="center">
																<IconButton
																	size="small"
																	onClick={() => handleViewDetails(employee)}
																>
																	<InfoOutlinedIcon fontSize="small" />
																</IconButton>
															</TableCell>
														</TableRow>
													);
												})
										)}
									</TableBody>
								</Table>
							</TableContainer>
						)}

						{/* Pagination */}
						<Box
							sx={{ display: "flex", justifyContent: "center", mt: 3 }}
						>
							<Pagination
								color="primary"
								count={Math.ceil(sortedAndFilteredCalculations.length / itemsPerPage)}
								page={currentPage}
								onChange={(event, value) => setCurrentPage(value)}
							/>
						</Box>
					</CardContent>
				</Card>

				{/* Calculation summary (reconciliation) */}
				{reconciliation && (
					<Box sx={{ mt: 3 }}>
						{isMobile ? (
							<PayrollRunReconciliationMobile
								reconciliation={reconciliation}
								employeeCount={employeeCount}
								formatEUR={formatEUR}
							/>
						) : (
							<>
								<Typography variant="subtitle1" fontWeight={700} sx={{ textAlign: "left" }}>
									{t('payroll.run.reconciliation.sectionTitle')}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "left", mb: 2 }}>
									{t('payroll.run.reconciliation.subtitle', { count: employeeCount })}
								</Typography>
								<PayrollRunReconciliation reconciliation={reconciliation} formatEUR={formatEUR} />
							</>
						)}
					</Box>
				)}

				{/* Finalize action — not shown until there's something calculated to finalize */}
				{reconciliation && !isMobile && (
					<Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
						<Tooltip title={!isAllBranchesSelected ? t('payroll.run.finalize.allBranchesRequired') : ""}>
							<span>
								<Button
									variant="contained"
									color="success"
									startIcon={<FactCheckIcon />}
									onClick={handleOpenFinalizeDialog}
									disabled={isFinalizeDisabled}
								>
									{t('payroll.run.finalizeButton')}
								</Button>
							</span>
						</Tooltip>
					</Box>
				)}
			</Box>

			{/* Finalize confirmation dialog */}
			<Dialog open={finalizeDialogOpen} onClose={handleCloseFinalizeDialog}>
				<DialogTitle>{t('payroll.run.finalize.confirmTitle')}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t('payroll.run.finalize.confirmDescription')}</DialogContentText>
					{!isPeriodLocked && (
						<Alert severity="warning" sx={{ mt: 2 }}>
							{t('payroll.run.finalize.willLockPeriodNotice')}
						</Alert>
					)}
					{finalizeError && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{finalizeError}
						</Alert>
					)}
					{finalizeInfo && !finalizeError && (
						<Alert severity="success" sx={{ mt: 2 }}>
							{finalizeInfo}
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseFinalizeDialog} disabled={finalizing}>
						{t('payroll.run.finalize.cancelButton')}
					</Button>
					<Button
						variant="contained"
						color="success"
						onClick={handleConfirmFinalize}
						disabled={finalizing}
						startIcon={finalizing ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						{finalizing ? t('payroll.run.finalizing') : t('payroll.run.finalize.confirmButton')}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Finalize success Snackbar */}
			<Snackbar
				open={finalizeSuccessMessage !== ""}
				autoHideDuration={4000}
				onClose={() => setFinalizeSuccessMessage("")}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Alert onClose={() => setFinalizeSuccessMessage("")} severity="success" sx={{ width: "100%" }}>
					{finalizeSuccessMessage}
				</Alert>
			</Snackbar>

			{/* Details Dialog */}
			<PayrollRunDetailsPerEmployee
				open={detailsDialogOpen}
				onClose={() => setDetailsDialogOpen(false)}
				employee={selectedEmployee}
				periodLabel={selectedPeriod}
				statusLabel={periodStatusLabel}
				isPeriodOpen={isPeriodOpen}
				contract={
					contracts.find((c) => c.contractId === selectedEmployee?.contractId) ?? null
				}
				formatEUR={formatEUR}
			/>

			{/* Columns dialog */}
			<PayrollRunColumnsDialog
				open={columnsDialogOpen}
				onClose={() => setColumnsDialogOpen(false)}
				isColumnVisible={isColumnVisible}
				handleToggleColumnVisibility={handleToggleColumnVisibility}
				handleShowAllColumns={handleShowAllColumns}
			/>

			{/* Sticky mobile action bar */}
			{isMobile && (
				<Box
					ref={bottomBarRef}
					sx={{
						position: "fixed", left: 0, right: 0, bottom: 0, p: 1.5,
						backgroundColor: "background.paper", borderTop: "1px solid", borderColor: "divider",
						zIndex: (t2) => t2.zIndex.appBar,
						display: "flex", gap: 1,
					}}
				>
					<Button
						fullWidth
						variant="contained"
						onClick={handleCalculatePayroll}
						disabled={calculating}
						startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
					>
						{calculating ? t('payroll.run.calculating') : t('payroll.run.calculateButton')}
					</Button>
					<IconButton
						sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
						onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
						aria-label={t('payroll.run.mobile.moreActions')}
					>
						<MoreVertIcon />
					</IconButton>
					<Menu anchorEl={mobileMenuAnchor} open={!!mobileMenuAnchor} onClose={() => setMobileMenuAnchor(null)}>
						<MenuItem
							disabled={downloading}
							onClick={() => {
								setMobileMenuAnchor(null);
								downloadPayroll();
							}}
						>
							{downloading ? t('common.downloading') : t('common.actions.download')}
						</MenuItem>
						{reconciliation && (
							<MenuItem
								disabled={isFinalizeDisabled}
								onClick={() => {
									setMobileMenuAnchor(null);
									handleOpenFinalizeDialog();
								}}
							>
								{t('payroll.run.finalizeButton')}
							</MenuItem>
						)}
					</Menu>
				</Box>
			)}
		</>
	);
}
